import { HeroMain, HeroMainProps } from '@sbercloud/uikit-product-site-hero';
import { WarningSVG } from '@snack-uikit/icons';
import { useRouter } from 'next/navigation';
import { type AnchorMenuProps } from 'src/components/ProductDetailed/AnchorMenu';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { type ClickAnalyticsType } from 'src/types';
import {
  type ProductPlatform,
  type ProductTag,
} from 'src/types/backendContent';

import { useHeaderPosition } from '../../hooks/useHeaderPosition';
import { scrollToElementWithId } from '../../utils/scrollToElementWithId';
import { type Breadcrumb } from '../Breadcrumbs';
import { Target, Variant } from '../Button/constants';
import { Ruble, SpeechBubble2 } from '../icons/ContentIcons/TechIcons';

export type ButtonProps = {
  title: string;
  variant?: Variant;
  className?: string;
  href?: string;
  target?: Target;
  analyticsOptions: ClickAnalyticsType;
  onClick?: () => void;
};

type ServiceType = {
  icon?: string;
  title: string;
};

type PlatformsType = {
  id: string;
  title: ProductPlatform;
  slug: string;
};

export type ProductHeroProps = {
  description?: string;
  breadcrumbs?: Breadcrumb[];
  platforms?: PlatformsType[];
  withPlatforms?: boolean;
  image?: string;
  service: ServiceType;
  className?: string;
  isLegal?: boolean;
  anchorMenu: AnchorMenuProps;
  available?: boolean;
  tags?: ProductTag[];
  buttons: (ButtonProps | null)[];
  backgroundColor?: HeroMainProps['backgroundColor'];
};

export function ProductHero({
  description,
  breadcrumbs = [],
  platforms = [],
  withPlatforms = true,
  image,
  buttons,
  service,
  tags,
  anchorMenu,
  available,
  backgroundColor,
}: ProductHeroProps) {
  const layoutType = useLayoutType();
  const { clickAnalytics } = useAnalytics();
  const router = useRouter();
  const { showHeader, headerHeight } = useHeaderPosition();

  const imgSrc = image || '/plugs/3d-model-default.png';

  const getNavbarProps = (): HeroMainProps['navbar'] | undefined => {
    if (!anchorMenu) {
      return undefined;
    }

    const anchorItems: NonNullable<HeroMainProps['navbar']>['items'] =
      anchorMenu.anchors.map((anchor) => {
        let icon;

        if (anchor.type === 'price') {
          icon = <Ruble />;
        }

        if (anchor.type === 'question') {
          icon = <SpeechBubble2 />;
        }

        return {
          id: anchor.id,
          text: anchor.name,
          icon,
        };
      });

    return {
      items: anchorItems,
      onItemClick: (id, name) => {
        clickAnalytics({
          action: 'click',
          clickZone: 'body',
          clickElement: 'navbar',
          clickContent: name,
          uniqueId: `${anchorMenu?.slug}-navbar-${name}`,
          transitionType: 'inside-link',
        });

        scrollToElementWithId(id);
      },
      topPosition: showHeader ? headerHeight : undefined,
    };
  };

  const breadcrumbsItems: HeroMainProps['breadcrumbs'] =
    breadcrumbs?.map((link) => ({
      id: link.title ?? link.link ?? '',
      href: link.link,
      label: link.title ?? '',
      onClick(e) {
        if (link.link && !e.metaKey) {
          e.preventDefault();
          router.push(link.link);
        }
      },
    })) ?? [];

  const buttonsForProduct: HeroMainProps['buttons'] = buttons
    ?.filter((button) => button !== null)
    .map(({ analyticsOptions, ...button }) => ({
      label: button.title,
      href: button.href ?? '',
      target: button.target,
      onClick(e) {
        if (button.href && button.target !== '_blank') {
          e.preventDefault();
          router.push(button.href);
        }

        clickAnalytics(analyticsOptions);
        button.onClick?.();
      },
    })) as HeroMainProps['buttons'];

  const getPlatformsProps = (): Pick<
    HeroMainProps,
    'platforms' | 'handlePlatformClick'
  > => {
    if (withPlatforms && platforms.length > 1) {
      return {
        platforms: platforms.map((platform) => ({
          id: platform.id,
          title: platform.title,
          href: `/${platform.slug}`,
        })),
        handlePlatformClick: (e, platform) => {
          e.preventDefault();
          router.push(platform.href);

          clickAnalytics({
            action: platform.href,
            clickZone: 'body',
            clickElement: 'button',
            clickContent: service.title,
            uniqueId: `product-detail-hero-platform-link`,
            transitionType: 'inside-link',
          });
        },
      };
    }

    return {};
  };

  return (
    <HeroMain
      backgroundColor={backgroundColor ?? 'neutral-background'}
      layoutType={layoutType}
      title={service.title}
      description={description ?? ''}
      image={imgSrc}
      navbar={getNavbarProps()}
      breadcrumbs={breadcrumbsItems}
      buttons={
        available
          ? buttonsForProduct
          : [
              {
                label: 'Временно недоступен',
                icon: <WarningSVG />,
                disabled: true,
              },
            ]
      }
      tags={tags?.map((tag) => ({
        text: tag.label ?? '',
        appearance: tag.appearance ?? 'neutral',
        tip: tag?.tooltip,
      }))}
      {...getPlatformsProps()}
    />
  );
}
